import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ResourceInterface } from "../../domain/interface/resource_interface";
import ThemeConstants from "../../../../config/theme/theme_constants";
import { markdownComponents } from "./markdown_components";
import { useTranslation } from "../../../../i18n/useTranslation";

interface Props {
  resource: ResourceInterface;
  onCompleted: (id: string) => void;
}

const TICK_MS = 100;

const SecureTextReader: React.FC<Props> = ({ resource, onCompleted }) => {
  const { t } = useTranslation();
  const theme = ThemeConstants();
  const isUnlocked = resource.viewed === true;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasCompletedRef = useRef(false);

  const documentUrl = resource.content && /^https?:\/\//.test(resource.content) ? resource.content : null;
  const text = resource.description ?? (documentUrl ? "" : resource.content) ?? "";

  const minReadSec = useMemo(() => {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(10, Math.ceil((words / 200) * 60));
  }, [text]);

  const [elapsed, setElapsed] = useState(0);
  const [bottomReached, setBottomReached] = useState(false);

  useEffect(() => {
    if (isUnlocked) return;
    const id = window.setInterval(() => {
      if (!document.hidden) setElapsed((s) => s + TICK_MS / 1000);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [isUnlocked]);

  useEffect(() => {
    if (isUnlocked) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setBottomReached(true);
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isUnlocked]);

  useEffect(() => {
    if (isUnlocked || hasCompletedRef.current) return;
    if (elapsed >= minReadSec && bottomReached) {
      hasCompletedRef.current = true;
      onCompleted(resource._id);
    }
  }, [elapsed, bottomReached, minReadSec, onCompleted, resource._id, isUnlocked]);

  const timePct = Math.min(elapsed / minReadSec, 1) * 100;
  const elapsedDisplay = Math.min(Math.floor(elapsed), minReadSec);

  return (
    <div className={`w-full flex flex-col items-center ${isUnlocked ? "pb-[5.6dvh]" : "pb-[12dvh]"}`}>
      {resource.picture && (
        <img
          src={resource.picture}
          alt={t('course.reader.image_alt', { title: resource.title })}
          className="w-full object-cover max-h-[45dvh]"
        />
      )}

      <div className="flex flex-col gap-4 items-center pt-[6dvh] max-lg:px-[3%] lg:px-[1%]">
        <h1
          className="font-bold text-2xl leading-tight text-center"
          style={{ color: theme.UvibesPink }}
        >
          {resource.title}
        </h1>
        <div className="prose prose-lg max-w-153">
          <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
        </div>
        {documentUrl && (
          <a
            href={documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="rounded-full text-white font-bold px-8 py-2.5 text-center cursor-pointer"
            style={{ backgroundColor: theme.UvibesPink }}
          >
            {t('course.reader.download_document')}
          </a>
        )}
        <div ref={sentinelRef} className="h-1 w-full" />
      </div>

      {!isUnlocked && (
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-white/95 backdrop-blur-sm border-t border-gray-100 px-4 pt-2 pb-[2.5dvh] flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>
            {t('course.reader.progress', { elapsed: elapsedDisplay, total: minReadSec })}
          </span>
          <span>{bottomReached ? t('course.reader.bottom_reached') : t('course.reader.scroll_hint')}</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${timePct}%`,
              background: theme.UvibesPink,
              transition: `width ${TICK_MS}ms linear`,
            }}
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default SecureTextReader;
